import { MenuToggle } from "@modules/app/components/MenuToggle"
import { AppMenuIcon } from "@modules/app/components/menu/appMenuIcon"
import { AppMenuItem } from "@modules/app/components/menu/appMenuItem"
import { AppMenuProfile } from "@modules/app/components/menu/appMenuProfile"
import { LayoutContext } from "@modules/app/context/layoutcontext"
import { MenuProvider } from "@modules/app/context/menucontext"
import { useMenuModels } from "@modules/app/hooks/useMenuModel"
import { MenuButtonsRef } from "@modules/app/types/layout"
import { MenuModel } from "@modules/app/types/types"
import { useBoxs, useCurrentBox } from "@modules/box/useBox"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { StripeAccountErrorItem } from "@modules/finances/components/stripeAccountErrorAlert"
import { getStripeAccountErrorText } from "@modules/finances/utils/financeUtils"
import { Path } from "@modules/navigation/routes"
import { useBoxNavigate } from "@modules/navigation/useBoxNavigate"
import { HUText } from "@modules/ui/components/huText"
import { HUStack } from "@modules/ui/components/layout"
import { colorWithAlpha } from "@modules/ui/styles/colors"
import { useContext, useRef } from "react"
import { useNavigate } from "react-router-dom"
import styled, { useTheme } from "styled-components"

type Props = {
  ownerMenu: boolean | undefined
}

type AppSubMenuProps = {
  menuModels: MenuModel[]
}

export const AppMenu: React.FC<Props> = ({ ownerMenu }) => {
  const menuModels: MenuModel[] = useMenuModels(ownerMenu)
  const menuToggleRef = useRef<MenuButtonsRef>(null)
  return (
    <>
      <AppMenuIcon />
      <MenuToggle ref={menuToggleRef} />
      <MenuContainer>
        <MenuProvider>
          <AppSubMenu menuModels={menuModels} />
        </MenuProvider>
        <OwnerMenu />
      </MenuContainer>
      <MenuFooter>
        <AppMenuProfile />
      </MenuFooter>
    </>
  )
}

const AppSubMenu: React.FC<AppSubMenuProps> = ({ menuModels }) => {
  return (
    <MenuProvider>
      <ul className="layout-menu ">
        {menuModels.map((item, i) => {
          return !item.seperator ? (
            <AppMenuItem item={item} root index={i} key={item.label} />
          ) : (
            <li className="menu-separator"></li>
          )
        })}
      </ul>
    </MenuProvider>
  )
}

const OwnerMenu: React.FC = () => {
  const { layoutState } = useContext(LayoutContext)
  const { result: boxs } = useBoxs()
  const hasBoxs = !!boxs && boxs.length > 0
  const theme = useTheme()
  const { result: box } = useCurrentBox()
  const boxNavigate = useBoxNavigate()
  const navigate = useNavigate()
  const { browserProfileService } = useServicesContext()

  const openStripeAccount = () => {
    boxNavigate(Path.Box.Admin + "?tab=banking")
  }

  const onCreateBox = () => {
    browserProfileService.setNewTheme("owner")
    setTimeout(() => navigate(Path.CreateBox), 100)
  }

  return !box && !hasBoxs && !layoutState.staticMenuDesktopInactive ? (
    <>
      <div className="flex flex-1" />
      <OwnerMenuContainer>
        <HUStack gap={[1, 5]} align>
          <HUText fontStyle="BL" className="text-center">
            {word("menu.owner.title")}
          </HUText>
          <HUText fontStyle="BM" color={theme.neutralColor600} className="text-center">
            {word("menu.owner.description")}
          </HUText>
          <div
            className="flex p-2 rounded-full bg-purple-500 transition-all transition-duration-150 hover:bg-purple-600 cursor-pointer"
            onClick={onCreateBox}
          >
            <span className="pi pi-calendar" style={{ color: theme.mainWhite }} />
          </div>
        </HUStack>
      </OwnerMenuContainer>
    </>
  ) : box?.accountError && !layoutState.staticMenuDesktopInactive ? (
    <div className="py-2 px-3">
      <StripeAccountErrorItem
        orientation="vertical"
        alertTitle={getStripeAccountErrorText(box.accountError).alertTitle}
        alertContent={getStripeAccountErrorText(box.accountError).alertContent}
        primaryButtonLabel={getStripeAccountErrorText(box.accountError).primaryButtonLabel}
        primaryButtonOnClickFn={openStripeAccount}
        size="S"
      />
    </div>
  ) : null
}

const MenuContainer = styled.div`
  overflow-y: auto;
  display: flex;
  flex: 1;
  flex-direction: column;
`

const OwnerMenuContainer = styled.div`
  margin: 5px 24px 10px;
  padding: 10px 16px;
  border-radius: 21px;
  background: ${({ theme }) =>
    `linear-gradient(142deg, ${theme.neutralColor100} 3%, ${colorWithAlpha(theme.neutralColor100, 0)} 96%)`};
`

const MenuFooter = styled.div`
  border-top: 1px solid var(--surface-border);
`
